import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Pin } from "lucide-react";

import { ConfirmDialog } from "~/components/ConfirmDialog";
import { usePortalRoot } from "~/components/EmbedPortalContext";
import type { PinnedMessagePreview } from "~/hooks/usePinned";
import { usePortalPlacement } from "~/lib/portal-positioning";
import { ICON_STROKE } from "~/lib/icon";

const POPOVER_WIDTH = 360;
const POPOVER_MAX_HEIGHT = 400;
const VIEWPORT_EDGE = 8;
const ANCHOR_OFFSET = 8;

export interface PinnedPanelProps {
  anchor: HTMLElement | null;
  pins: PinnedMessagePreview[];
  canManage: boolean;
  onJump: (eventId: string) => void;
  onUnpinAll: () => Promise<void>;
  onClose: () => void;
}

export function PinnedPanel({
  anchor,
  pins,
  canManage,
  onJump,
  onUnpinAll,
  onClose,
}: PinnedPanelProps) {
  const portalRoot = usePortalRoot();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const hasFocusedRef = useRef(false);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const placement = usePortalPlacement({
    anchor,
    portalRoot,
    computeViewportCoords: (rect, surface) => {
      const measured = panelRef.current?.getBoundingClientRect();
      const height = Math.min(measured?.height ?? POPOVER_MAX_HEIGHT, POPOVER_MAX_HEIGHT);
      const surfaceBottom = surface.offsetTop + surface.height;
      const surfaceRight = surface.offsetLeft + surface.width;
      const flipsUp = surfaceBottom - rect.bottom < height + ANCHOR_OFFSET;
      const viewportTop = flipsUp
        ? Math.max(surface.offsetTop + VIEWPORT_EDGE, rect.top - height - ANCHOR_OFFSET)
        : rect.bottom + ANCHOR_OFFSET;
      const rawLeft = rect.right - POPOVER_WIDTH;
      const maxLeft = surfaceRight - POPOVER_WIDTH - VIEWPORT_EDGE;
      const viewportLeft = Math.max(surface.offsetLeft + VIEWPORT_EDGE, Math.min(rawLeft, maxLeft));
      return { viewportTop, viewportLeft };
    },
  });

  useEffect(() => {
    if (!placement || hasFocusedRef.current) return;
    hasFocusedRef.current = true;
    const firstButton = panelRef.current?.querySelector<HTMLButtonElement>("button");
    if (firstButton) {
      firstButton.focus();
    } else {
      panelRef.current?.focus();
    }
  }, [placement]);

  useEffect(() => {
    return () => {
      anchor?.focus?.();
    };
  }, [anchor]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        if (confirmingClear) return;
        event.preventDefault();
        onClose();
        anchor?.focus?.();
        return;
      }
      if (event.key !== "Tab") return;
      if (confirmingClear) return;
      const target = event.target as Node | null;
      if (!panelRef.current?.contains(target)) return;
      event.preventDefault();
      onClose();
      anchor?.focus?.();
    }
    function onPointerDown(event: MouseEvent) {
      if (confirmingClear) return;
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (anchor?.contains(target)) return;
      onClose();
    }
    function onFocusOut(event: FocusEvent) {
      if (confirmingClear) return;
      const related = event.relatedTarget as Node | null;
      if (!related) return;
      if (panelRef.current?.contains(related) || anchor?.contains(related)) return;
      onClose();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, [anchor, confirmingClear, onClose]);

  const content = useMemo(() => {
    if (!placement) return null;
    return (
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Pinned messages"
        tabIndex={-1}
        style={{
          position: placement.position,
          top: placement.top,
          left: placement.left,
          width: POPOVER_WIDTH,
          maxHeight: POPOVER_MAX_HEIGHT,
          zIndex: 50,
          display: "flex",
          flexDirection: "column",
          background: "var(--chat-surface-overlay)",
          borderRadius: "var(--chat-radius-xl)",
          boxShadow: "var(--chat-shadow-popover)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: 44,
            display: "flex",
            alignItems: "center",
            gap: "var(--chat-space-2)",
            padding: "0 var(--chat-space-3)",
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
            fontWeight: 500,
            flexShrink: 0,
          }}
        >
          <Pin size={16} strokeWidth={ICON_STROKE} style={{ color: "var(--chat-fg-secondary)" }} />
          <span>Pinned messages · {pins.length}</span>
        </div>
        <div style={{ overflowY: "auto", minHeight: 0 }}>
          {pins.length === 0 ? (
            <p
              style={{
                margin: 0,
                padding: "var(--chat-space-4)",
                color: "var(--chat-fg-muted)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-sm-size)",
                lineHeight: "var(--chat-type-body-sm-line)",
              }}
            >
              No pinned messages
            </p>
          ) : (
            pins.map((pin) => (
              <PinnedRow key={pin.eventId} pin={pin} onJump={onJump} onClose={onClose} />
            ))
          )}
        </div>
        {canManage && pins.length > 0 && (
          <div
            style={{
              padding: "var(--chat-space-2) var(--chat-space-3)",
              flexShrink: 0,
            }}
          >
            <button
              type="button"
              onClick={() => {
                setConfirmingClear(true);
              }}
              className="chat-button"
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                color: "var(--chat-state-danger)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-sm-size)",
                lineHeight: "var(--chat-type-body-sm-line)",
                cursor: "pointer",
              }}
            >
              Unpin all
            </button>
          </div>
        )}
      </div>
    );
  }, [canManage, onClose, onJump, pins, placement]);

  if (!anchor || !portalRoot) return null;
  return createPortal(
    <>
      {content}
      {confirmingClear && (
        <ConfirmDialog
          title="Unpin all messages?"
          body="This removes every pinned message from this room. It can't be undone."
          confirmLabel="Unpin all"
          onConfirm={() => {
            void onUnpinAll()
              .then(() => {
                setConfirmingClear(false);
                onClose();
              })
              .catch(() => {
                setConfirmingClear(false);
              });
          }}
          onCancel={() => setConfirmingClear(false)}
        />
      )}
    </>,
    portalRoot,
  );
}

function PinnedRow({
  pin,
  onJump,
  onClose,
}: {
  pin: PinnedMessagePreview;
  onJump: (eventId: string) => void;
  onClose: () => void;
}) {
  return (
    <div
      role={pin.status === "loaded" ? undefined : "status"}
      aria-live={pin.status === "loaded" ? undefined : "polite"}
      style={{
        minHeight: 72,
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: "var(--chat-space-3)",
        alignItems: "center",
        padding: "var(--chat-space-3)",
      }}
    >
      <div data-feedback-redact="true" style={{ minWidth: 0 }}>
        <div
          style={{
            color: "var(--chat-fg-secondary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {pin.sender ?? "Unknown sender"}
        </div>
        <div
          style={{
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {pin.body}
        </div>
      </div>
      <button
        type="button"
        onClick={() => {
          onJump(pin.eventId);
          onClose();
        }}
        className="chat-button"
        style={{
          border: "none",
          background: "transparent",
          padding: 0,
          color: "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
          cursor: "pointer",
        }}
      >
        Jump
      </button>
    </div>
  );
}
